import { useState, useEffect } from "react";
import { Shield, BarChart3, ThumbsUp, Package } from "lucide-react";

const CAROUSEL_IMAGES = [
  { src: "https://static.prod-images.emergentagent.com/jobs/b9fd19fa-914b-40ea-b71c-333af0dc61ff/images/2759fb2e7d60a07398dc1511ee2c379401642a08bb2d85fd7e06150f8a1cf0a5.png", alt: "Zeel founder" },
  { src: "https://static.prod-images.emergentagent.com/jobs/7ee131c6-4bab-4dd1-83de-596734e4340a/images/9f1cb1205fedb9458d792d28c3220a32fc01fd2cc36095986f4f88a023ecaba7.png", alt: "Kavya" },
  { src: "https://static.prod-images.emergentagent.com/jobs/7ee131c6-4bab-4dd1-83de-596734e4340a/images/376cd07fcd01f09d520486e1b78a700c4eaa1a03dd8451103b7ca8ebd085edb2.png", alt: "Ananya" },
  { src: "https://static.prod-images.emergentagent.com/jobs/7ee131c6-4bab-4dd1-83de-596734e4340a/images/8e8650a0cc577d4810d8dfdd8069fd4a25b43b3efc9bc0f2dc3d4f682ad5a280.png", alt: "Arjun" },
  { src: "https://static.prod-images.emergentagent.com/jobs/7ee131c6-4bab-4dd1-83de-596734e4340a/images/8c97fa7f0c840a5a1ca41668c428c42906a9e9b3a505ab957a5311810b2356b3.png", alt: "Meera" },
  { src: "https://static.prod-images.emergentagent.com/jobs/7ee131c6-4bab-4dd1-83de-596734e4340a/images/1ae613899f0738142f380c038c5528a5eee407d66b7375a9a446fdcc2ec8ef05.png", alt: "Vikram" },
];

export const WhyZeelSection = () => {
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIdx(prev => (prev + 1) % CAROUSEL_IMAGES.length);
    }, 2000);
    return () => clearInterval(timer);
  }, []);
  return (
    <section className="py-20 px-4 bg-white" data-testid="why-zeel-section">
      <div className="max-w-4xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,1.6fr] gap-12 items-center">
          {/* Left — carousel */}
          <div className="relative flex justify-center" style={{ overflow: "visible" }}>
            <img
              src="https://uxdesignlabs.com/wp-content/uploads/2024/07/zeel-logo.svg"
              alt=""
              aria-hidden="true"
              className="absolute select-none pointer-events-none z-[5]"
              style={{ width: "420px", bottom: "10%", left: "50%", transform: "translateX(-50%)", opacity: 0.3 }}
            />
            <div className="relative w-[240px] h-[300px] sm:w-[280px] sm:h-[350px]">
              <div className="relative w-full h-full rounded-3xl overflow-hidden shadow-xl z-[1]">
                {CAROUSEL_IMAGES.map((img, i) => (
                  <img
                    key={img.alt || `carousel-${i}`}
                    src={img.src}
                    alt={img.alt}
                    className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ease-in-out"
                    style={{ opacity: i === activeIdx ? 1 : 0 }}
                  />
                ))}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>
              {/* Dots */}
              <div className="flex justify-center gap-1.5 mt-4">
                {CAROUSEL_IMAGES.map((img, i) => (
                  <button
                    key={`dot-${img.alt || i}`}
                    onClick={() => setActiveIdx(i)}
                    className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${i === activeIdx ? "bg-[#5B6BA8] w-4" : "bg-slate-300"}`}
                    data-testid={`carousel-dot-${i}`}
                  />
                ))}
              </div>
            </div>
          </div>

        {/* Right — text */}
        <div>
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#5B6BA8] mb-4">Why We Created Zeel</p>
          <h2 className="text-2xl sm:text-3xl font-semibold mb-5 leading-tight text-gradient-brand">
            Skincare advice today is broken.
          </h2>
          <div className="space-y-4 text-muted-foreground text-sm sm:text-base leading-relaxed">
            <p><strong className="text-foreground">Every brand claims their product works.</strong> Paid promotions and brand-incentivized reviews make it nearly impossible to know what actually does.</p>
            <p><strong className="text-foreground">Most reviews don't know your skin.</strong> Generic advice ignores your skin type, your climate, your concerns — written for everyone, which means useful for no one.</p>
            <p><strong className="text-foreground">And products don't work in isolation.</strong> What cleared someone's skin was a routine — not one single magic serum.</p>

            <div className="border-t border-slate-200 pt-5 mt-5">
              <p>
                <strong className="text-foreground">Zeel exists to fix that.</strong> Routines and product insights shared by people with skin like yours, voted on by the community — so you can make decisions backed by what people actually experienced. Not just brand promises.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2.5 mt-8">
            {[
              { icon: Shield, label: "Honest Experiences" },
              { icon: BarChart3, label: "Real Product Signals" },
              { icon: ThumbsUp, label: "Community Validation" },
              { icon: Package, label: "Effective Routines" },
            ].map(({ icon: Icon, label }) => (
              <span key={label} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#5B6BA8]/[0.06] text-xs font-medium text-[#5B6BA8]">
                <Icon className="w-3.5 h-3.5" /> {label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};
